import { Injectable } from '@angular/core';
import {
  BOOKING_ERROR,
  IBookableService,
  IBookableServiceListParams,
  ICreateBookableServicePayload,
  IPaginatedResponse,
  IUpdateBookableServicePayload
} from '@pharvio/core/shared';
import { Observable, delay, of, throwError } from 'rxjs';
import { BOOKABLE_SERVICE_FIXTURES } from './fixtures/bookings.fixtures';
import { BookableServicesDataService } from './repositories/bookable-services-data.service';

const TAX_CLASSES: Record<string, IBookableService['taxClass']> =
  Object.fromEntries(
    BOOKABLE_SERVICE_FIXTURES.map((s) => [s.taxClass.id, s.taxClass])
  );

@Injectable()
export class InMemoryBookableServicesDataService extends BookableServicesDataService {
  private services = [...BOOKABLE_SERVICE_FIXTURES];

  getServices(
    params: IBookableServiceListParams = {}
  ): Observable<IPaginatedResponse<IBookableService>> {
    const search = params.search?.trim().toLowerCase();
    const rows = this.services
      .filter((s) => (search ? s.name.toLowerCase().includes(search) : true))
      .filter((s) =>
        params.isActive === undefined ? true : s.isActive === params.isActive
      )
      .filter((s) =>
        params.sellableOnline === undefined
          ? true
          : s.sellableOnline === params.sellableOnline
      )
      // Empty `branchIds` means every branch, so it matches any filter.
      .filter((s) =>
        params.branchId
          ? s.branchIds.length === 0 || s.branchIds.includes(params.branchId)
          : true
      )
      .sort((a, b) => a.displayOrder - b.displayOrder);

    return of(this.paginate(rows, params.page ?? 1, params.size ?? 25)).pipe(
      delay(150)
    );
  }

  getService(id: string): Observable<IBookableService> {
    const row = this.services.find((s) => s.id === id);
    return row
      ? of(row).pipe(delay(150))
      : throwError(() => ({
          statusCode: 404,
          code: BOOKING_ERROR.SERVICE_NOT_FOUND,
          message: 'That service no longer exists.'
        }));
  }

  createService(
    payload: ICreateBookableServicePayload
  ): Observable<IBookableService> {
    const clash = this.services.find(
      (s) =>
        s.name.trim().toLowerCase() === payload.name.trim().toLowerCase()
    );
    if (clash) {
      return throwError(() => ({
        statusCode: 409,
        code: BOOKING_ERROR.SERVICE_NAME_TAKEN,
        message: `A service called "${clash.name}" already exists.`,
        fieldErrors: [{ field: 'name', message: 'That name is taken.' }]
      }));
    }

    const row: IBookableService = {
      id: crypto.randomUUID(),
      name: payload.name,
      description: payload.description ?? null,
      priceMinor: payload.priceMinor,
      durationMinutes: payload.durationMinutes,
      depositPercent: payload.depositPercent,
      taxClass: TAX_CLASSES[payload.taxClassId] ?? {
        id: payload.taxClassId,
        code: 'UNKNOWN',
        displayName: 'Unknown',
        rateBps: 0,
        source: 'tenant'
      },
      isActive: payload.isActive ?? true,
      sellableOnline: payload.sellableOnline ?? true,
      displayOrder: payload.displayOrder ?? this.services.length + 1,
      branchIds: payload.branchIds ?? [],
      createdBy: { kind: 'user', name: 'You' },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    this.services = [...this.services, row];
    return of(row).pipe(delay(150));
  }

  updateService(
    id: string,
    payload: IUpdateBookableServicePayload
  ): Observable<IBookableService> {
    const row = this.services.find((s) => s.id === id);
    if (!row) {
      return throwError(() => ({
        statusCode: 404,
        code: BOOKING_ERROR.SERVICE_NOT_FOUND,
        message: 'That service no longer exists.'
      }));
    }
    const updated: IBookableService = {
      ...row,
      ...payload,
      description:
        payload.description === undefined
          ? row.description
          : (payload.description ?? null),
      taxClass: payload.taxClassId
        ? (TAX_CLASSES[payload.taxClassId] ?? row.taxClass)
        : row.taxClass,
      // A PATCH replaces the whole assignment set rather than merging it.
      branchIds: payload.branchIds ?? row.branchIds,
      updatedAt: new Date().toISOString()
    };
    this.services = this.services.map((s) => (s.id === id ? updated : s));
    return of(updated).pipe(delay(150));
  }

  deleteService(id: string): Observable<void> {
    // The one service with a live booking in the fixtures stands in for the
    // 409 the server raises, so the "deactivate instead" path is reachable.
    if (id === 'svc-med-review') {
      return throwError(() => ({
        statusCode: 409,
        code: BOOKING_ERROR.SERVICE_HAS_OPEN_BOOKINGS,
        message: 'This service still has open bookings.'
      }));
    }
    this.services = this.services.filter((s) => s.id !== id);
    return of(undefined).pipe(delay(150));
  }

  private paginate(
    rows: IBookableService[],
    page: number,
    size: number
  ): IPaginatedResponse<IBookableService> {
    const totalPages = Math.max(1, Math.ceil(rows.length / size));
    const data = rows.slice((page - 1) * size, page * size);
    return {
      data,
      meta: {
        page,
        size,
        totalItems: rows.length,
        totalPages,
        hasPreviousPage: page > 1,
        hasNextPage: page < totalPages,
        totalPageItems: data.length
      }
    };
  }
}
